
import { Box, Button, Center, Container, HStack, IconButton, Menu, MenuButton, MenuList, Tooltip, VStack, useBreakpointValue, useToast } from '@chakra-ui/react'
import { ICourse } from '@/shared/ICourse'
import React from 'react';

import "survey-core/survey-core.min.css";

import { Survey } from "survey-react-ui";
import { GetPanelTitleActionsEvent, GetQuestionTitleActionsEvent, Model } from "survey-core";
import { parseJSON, stringifyError } from '@/shared/parse';
import { TelemetryClientService } from '../services/TelemetryClientService';
import { CourseModuleStorageService } from '../services/CourseModuleStorageService';
import { WorkflowService } from '../../shared/WorkflowService';
import { ICourseModule } from '@/shared/ICourseModule';
import { SurveyService } from '../services/SurveyService';
import { CourseFileService } from '../services/CourseFileService';
import { PdfService } from '../services/PdfService';
import { CommentSidebar } from '../comments/CommentSidebar';
import { ICommentSet } from '@/shared/IComment';
import { CommentsService } from '@/app/services/CommentsService';
import { AppContext } from '../comments/AppContext';
import { CourseProgress } from './CourseProgress';
import { ButtonRefresh } from './ButtonRefresh';
import { FiMenu, FiMessageSquare } from 'react-icons/fi';
import { COMMENT_STATUS, STEPS } from '@/shared/constants';
import { AuthService } from '../services/AuthService';
import { MenuItemOpenSharePoint } from './MenuItemOpenSharePoint';

const isShowingPreview = (model: any) => model.isShowingPreview;

interface IDataToSave {
  data: any;
  nextStatus: string;
}

export const SurveyView = (props: {
  course: ICourse,
  module: ICourseModule,
  action: string | undefined,
  moduleTemplate: any,
  moduleDataProperty: keyof ICourseModule,
  moduleFileName: string,
  pdfFileName?: string,
  pdfFileTitle?: string,
  pageId?: string,
  enableComments: boolean,
  narrowCommentsPanel?: boolean;
  showMarkup?: boolean,
  showSubmit?: boolean,
  showMenu?: boolean,
  showFeedback?: boolean,
  isLoading?: boolean,
  onShowFeedback?: () => void,
  onPageChanged?: (pageId: string) => void,
  onComplete?: (data: any) => Promise<void>,
  onRefresh?: () => void,
  onCommentsSidebarOpen?: (isOpen: boolean) => void,
}) => {

  SurveyService.configure();

  const [model, setModel] = React.useState<Model>();

  const { setStatus } = React.useContext(AppContext);

  const allowFancyProgressBar = !!useBreakpointValue({ base: false, md: true }) && (model?.visiblePageCount || 0) > 1;

  const toast = useToast({
    duration: 1000,
  });

  const onSubmitData = async (debounedDataToSave: IDataToSave) => {
    const { data, nextStatus } = debounedDataToSave;

    try {
      setStatus(`Submitting...`);
      setIsBusy(true);

      const update = { ...props.module, [props.moduleDataProperty]: data };
      await CourseModuleStorageService.putCourseModule(props.course.id, update, props.moduleDataProperty, props.moduleFileName, nextStatus);

      const pdf = await PdfService.generateSurveyPdf(props.course.coursePlatformVersion, props.moduleTemplate, data);
      await CourseFileService.uploadFiles(props.course.id, props.module.id, [new File([pdf], props.pdfFileName!)], props.pdfFileTitle!, 'replace');

      if (props.onComplete) {
        await props.onComplete(data);
      }
    } finally {
      setStatus(undefined);
      setIsBusy(false);
    }
  }

  const onSaveData = async (debounedDataToSave: IDataToSave) => {
    const { data, nextStatus } = debounedDataToSave;
    try {
      setStatus(`Saving...`);
      const update = { ...props.module, [props.moduleDataProperty]: data };
      await CourseModuleStorageService.putCourseModule(props.course.id, update, props.moduleDataProperty, props.moduleFileName, nextStatus);
    } catch (err: any) {
      toast({
        title: `The last draft modifications was not saved.`,
        description: stringifyError(err),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setStatus(undefined)
    }
  }

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const dataToSaveRef = React.useRef<IDataToSave>();

  const setDataToSave = (update: IDataToSave) => {
    setStatus(`Modified...`);
    setTriggerCount(x => x + 1);
    dataToSaveRef.current = update;
    props.module[props.moduleDataProperty] = update.data;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (dataToSaveRef.current) {
        const { nextStatus } = dataToSaveRef.current;
        const isTransition = !!nextStatus && !nextStatus.endsWith('draft');
        if (isTransition) {
          toast.promise(onSubmitData(dataToSaveRef.current), {
            loading: { title: `Submitting the questionnaire...`, description: `Please wait while we generate the PDF snapshots...` },
            success: { title: `The questionnaire was submitted successfully.`, description: `Thank you for the patience!` },
            error: err => ({
              title: `Error submitting the questionnaire`,
              description: stringifyError(err)
            }),
          });
        } else {
          onSaveData(dataToSaveRef.current)
        }
        timeoutRef.current = null;
      }
    }, 1000);
  }

  const onValueChanged = (survey: Model, options: any) => {
    SurveyService.preprocessResults(survey, options);
    const data = survey.data;
    if (data && props.action !== 'view' && JSON.stringify(data) !== JSON.stringify(dataToSaveRef.current) && !isShowingPreview(survey)) {
      const nextStatus = WorkflowService.getDraftStatus(props.course);
      setDataToSave({ data, nextStatus });
    }
  }

  const onComplete = async (survey: any) => {
    survey.mergeData({}); // force recalculation of expressions
    const data = survey.data;
    const nextStatus = WorkflowService.getNextStatus(props.course, props.module.id, props.action, data);
    setDataToSave({ data, nextStatus });
  }

  const deleteFile = async (path: string, options: any) => {
    try {
      await CourseFileService.deleteFile(props.course.id, path);
      options.callback("success");
      return true;
    } catch (error: any) {
      if (error.statusCode === 404 || error.internalError?.statusCode === 404) {
        options.callback("success");
        return false;
      } else {
        options.callback("error");
        throw error;
      }
    }
  }

  const onClearFiles = async (_survey: any, options: any) => {
    if (!options.value || options.value.length === 0)
      return options.callback("success");
    if (!options.fileName && !!options.value) {
      for (const item of options.value) {
        if (item.content.startsWith('data:')) {
          options.callback("success");
        } else {
          toast.promise(deleteFile(item.content, options), {
            loading: { title: `Deleting file '${item.name}'` },
            success: deleted => ({
              title: deleted
                ? `File '${item.name}' deleted successfully`
                : `File '${item.name}' was already deleted`
            }),
            error: err => ({
              title: `Unable to delete file '${item.name}'`,
              description: stringifyError(err)
            }),
          });
        }
      }
    } else {
      const fileToRemove = options.value.find(
        (item: any) => item.name === options.fileName
      );
      if (fileToRemove) {
        if (fileToRemove.content.startsWith('data:')) {
          options.callback("success");
        } else {
          toast.promise(deleteFile(fileToRemove.content, options), {
            loading: { title: `Deleting file '${fileToRemove.name}'` },
            success: deleted => ({
              title: deleted
                ? `File '${fileToRemove.name}' deleted successfully`
                : `File '${fileToRemove.name}' was already deleted`
            }),
            error: err => ({
              title: `Unable to delete file '${fileToRemove.name}'`,
              description: stringifyError(err)
            }),
          });
        }
      } else {
        TelemetryClientService.warn(`File with name ${options.fileName} is not found, not deleted`);
        options.callback("error");
      }
    }
  }

  const [pageId, setPageId] = React.useState(props.pageId);
  React.useEffect(() => setPageId(props.pageId), [props.pageId]);

  const onCurrentPageChanged = async (_sender: any, options: any) => {
    setPageId(options.newCurrentPage.name);
    props.onPageChanged?.(options.newCurrentPage.name);
  }

  const onDownloadFile = async (_survey: any, options: any) => {
    try {
      const file = await CourseFileService.getFile(props.course.id, options.content);
      options.callback("success", file.downloadUrl);
    } catch (error) {
      TelemetryClientService.error(`Unable to download file ${options.content}`, error);
      toast({
        title: `Unable to resolve a file used in this survey: '${options.content}'`,
        description: stringifyError(error),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      options.callback("success");
    }
  }

  const uploadFile = async (options: any) => {
    try {
      const title = options.question.summaryLabel || options.question.processedTitle;
      const uploaded = await CourseFileService.uploadFiles(
        props.course.id,
        props.module.id,
        options.files,
        title,
        'rename'
      );

      options.callback(
        uploaded.map(file => {
          return {
            file: file,
            content: `${props.module.id}/${file.name}`
          };
        })
      );
    } catch (error) {
      TelemetryClientService.error(`Unable to upload file ${error}`, error);
      options.callback([], [`Unable to upload file. ${stringifyError(error)}`]);
    }
  }

  const onUploadFiles = async (_survey: any, options: any) => {
    toast.promise(uploadFile(options), {
      loading: { title: `Uploading file(s)` },
      success: { title: `File(s) uploaded successfully` },
      error: { title: `Unable to upload file(s)` },
    });
  }

  const onUpdatePageCssClasses = (survey: Model, options: any) => {
    if (survey.isDisplayMode === true && options.page.name === 'all' && options.cssClasses.page.root) {
      options.cssClasses.page.root += ' eitum-preview';
    }
  }

  const [isBusy, setIsBusy] = React.useState(true);

  const onAfterRenderSurvey = (survey: Model) => {

    // PATCH: does not update for some reason out of the box
    const courseMappingQuestion = survey.getQuestionByName('CourseMapping');
    if (courseMappingQuestion) {
      courseMappingQuestion.isAnswered = !!survey.data?.CourseMapping;
    }

    setIsBusy(false);
  }

  const processMarkdown = (survey: Model, options: any) => {

    options.html = SurveyService.processMarkdown(options.text);

    if (props.showMarkup) {
      const el = options.element;
      if (el && el.getType() === "page" && options.name === 'title') {
        options.html = SurveyService.getPageTitle(survey, el.name);
      }
    }
  }

  const load = () => {

    setIsBusy(true);
    const newModel = new Model(props.moduleTemplate);

    newModel.widthMode = 'responsive';

    if (pageId) {
      newModel.currentPage = newModel.getPageByName(pageId);
    }

    newModel.onDownloadFile.add(onDownloadFile);
    newModel.onTextMarkdown.add(processMarkdown);
    newModel.data = props.module[props.moduleDataProperty];

    if (props.action === 'review' || WorkflowService.getModuleIdFromStep(props.course.CourseStep) === STEPS.REVIEW) {
      newModel.showPreviewBeforeComplete = 'noPreview';
    }

    SurveyService.preprocessTemplate(newModel, props.module.id, props.action, props.showMarkup);

    if (props.action === 'view') {
      newModel.mode = 'display';
    }

    newModel.showTOC = false;
    newModel.progressBarType = 'none'

    // Initialize panel cache after survey is fully loaded
    CommentsService.initPanelCache(newModel);

    setModel(newModel)
  }

  React.useEffect(() => {
    if (props.module && props.course) {
      load();
    }
  }, [props.module, props.course]);

  const [questionId, setQuestionId] = React.useState<string>();

  const actionTitleRefs = React.useRef<{ [key: string]: { title: string } }>({});

  const [triggerCount, setTriggerCount] = React.useState(0);

  const getQuestionCommentTitle = (commentSet?: ICommentSet, name?: string) => {
    const commentCount = CommentsService.getQuestionCommentCount(commentSet, comment => comment.questionId === name && comment.moduleId === props.module.id);
    return commentCount > 0 ? `Comment (${commentCount})` : `Comment`;
  }

  const getItemTitleActions = (item: any, evt: GetPanelTitleActionsEvent | GetQuestionTitleActionsEvent) => {
    if (props.enableComments) {
      // Get the full hierarchical ID (works for both questions and panels)
      const fullQuestionId = CommentsService.getQuestionId(item);
      const isNested = fullQuestionId !== item.name;
      
      // Allow comments if: nested item OR top-level item with numbered title
      if (isNested || CommentsService.questionNeedsComment(item.title)) {
        evt.actions = evt.actions || [];
        evt.actions.push({
          title: getQuestionCommentTitle(props.course.commentSet, fullQuestionId),
          disableTabStop: true,
          action: (context) => {
            actionTitleRefs.current[fullQuestionId] = context;
            // Use setTimeout to ensure state updates happen outside of render cycle
            setTimeout(() => {
              setQuestionId(fullQuestionId);
              setTriggerCount(x => x + 1);
              setIsCommentsPanelOpen(true);
            }, 0);
          }
        });
      }
    }
  }

  const onGetPanelTitleActions = (_: Model, evt: GetPanelTitleActionsEvent) => {
    getItemTitleActions(evt.panel, evt)
  }

  const onGetQuestionTitleActions = (_: Model, evt: GetQuestionTitleActionsEvent) => {
    getItemTitleActions(evt.question, evt);
  }

  const [allCommentSet, _setAllCommentSet] = React.useState<ICommentSet>(props.course.commentSet ?? {});
  const setAllComments = (commentSet: ICommentSet) => {
    _setAllCommentSet(commentSet);
    for (const key in actionTitleRefs.current) {
      actionTitleRefs.current[key].title = getQuestionCommentTitle(commentSet, key);
    }
    if (props.course) {
      props.course.commentSet = commentSet;
    }
  }

  const haveOpenComments = props.enableComments && Object.values(allCommentSet).some(comment =>
    comment.moduleId === props.module.id && comment.status !== COMMENT_STATUS.RESOLVED && comment.status !== COMMENT_STATUS.COMMENT
  );
  const showCommentsByDefault = haveOpenComments && parseJSON(localStorage.getItem(`eitum-comments-open`) || '', haveOpenComments);
  const [isCommentdPanelOpen, _setIsCommentsPanelOpen] = React.useState(showCommentsByDefault);
  const setIsCommentsPanelOpen = (isOpen: boolean) => {
    _setIsCommentsPanelOpen(isOpen);
    props.onCommentsSidebarOpen?.(isOpen);
    localStorage.setItem(`eitum-comments-open`, JSON.stringify(isOpen));
  }

  const allowPin = !!useBreakpointValue({ base: false, md: true });
  const marginRight = isCommentdPanelOpen && props.enableComments && allowPin ? (props.narrowCommentsPanel ? '16rem' : '28rem') : 0;

  const onShowingPreview = (survey: Model) => {
    survey.validate(undefined, true)
    props.onPageChanged?.(survey.currentPage.name);
  }

  const onAfterRenderQuestion = (_: Model, options: any) => {
    if (!!options.question.inputType && options.question.inputType === "number") {
      const numericInput = options.htmlElement.getElementsByTagName("input")[0];
      numericInput.addEventListener('wheel', function () {
        numericInput.blur();
      });
    }
  }

  const onSubmit = (model: Model) => {
    if (!isShowingPreview(model)) {
      model.validate(undefined, true);
      model.showPreview();
    } else {
      if (model.validate(undefined, true)) {
        model.tryComplete();
      }
    }
  }

  const onCancelSubmit = (model: Model) => {
    if (!isShowingPreview(model)) {
      return;
    }
    model.cancelPreview();
  }

  return (model &&
    <VStack spacing='0' style={{ marginRight: marginRight, flex: 1 }}>
      <Container>
        <HStack my='4' justify='space-between' alignItems='flex-start'>
          {allowFancyProgressBar ? (!isShowingPreview(model)
            ? <CourseProgress isDisabled={isBusy} model={model} showMarkup={props.showMarkup} />
            : props.showSubmit && <Box w='full' fontSize='x-large' fontWeight='bold' ><Center>Preview & Submit</Center></Box>
          ) : <Box />}
          <HStack spacing={4}>
            {props.showFeedback && <Button isDisabled={isBusy} variant='outline' onClick={props.onShowFeedback}>{"Show Feedback"}</Button>}
            {props.showSubmit && isShowingPreview(model) && <Button isDisabled={isBusy} variant='outline' onClick={() => onCancelSubmit(model)}>{"Cancel"}</Button>}
            {props.showSubmit && <Button isDisabled={isBusy || props.isLoading} variant='solid' onClick={() => onSubmit(model)}>{!isShowingPreview(model) ? "Preview & Submit" : "Submit Now"}</Button>}
            {props.enableComments &&
              <Tooltip label="Comments" aria-label="Comments">
                <IconButton variant='ghost' onClick={() => setIsCommentsPanelOpen(true)} isDisabled={isBusy} icon={<FiMessageSquare />} aria-label={'Comments'} />
              </Tooltip>
            }
            {props.onRefresh && <ButtonRefresh isDisabled={isBusy || props.isLoading} onClick={props.onRefresh} />}
            {props.showMenu && <Menu>
              <MenuButton as={IconButton} isDisabled={!AuthService.isStaff} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
              <MenuList>
                <MenuItemOpenSharePoint relativeUrl={`Courses/${props.course.folderName}/${props.module.id}`} />
              </MenuList>
            </Menu>}
          </HStack>
        </HStack>
      </Container>
      <Survey
        model={model}
        onComplete={onComplete}
        onValueChanged={onValueChanged}
        onUploadFiles={onUploadFiles}
        onClearFiles={onClearFiles}
        onAfterRenderQuestion={onAfterRenderQuestion}
        onGetQuestionTitleActions={onGetQuestionTitleActions}
        onGetPanelTitleActions={onGetPanelTitleActions}
        onCurrentPageChanged={onCurrentPageChanged}
        onAfterRenderSurvey={onAfterRenderSurvey}
        onUpdatePageCssClasses={onUpdatePageCssClasses}
        onShowingPreview={onShowingPreview}
      />
      <CommentSidebar
        survey={model}
        allowPin={allowPin}
        courseId={props.course.id}
        moduleId={props.module.id}
        pageId={pageId}
        questionId={questionId}
        size={props.narrowCommentsPanel ? 'xs' : 'sm'}
        isOpen={isCommentdPanelOpen && props.enableComments}
        onClose={() => setIsCommentsPanelOpen(false)}
        allCommentSet={allCommentSet}
        triggerCount={triggerCount}
        setAllCommentSet={setAllComments}
      />
    </VStack>
  )
}